import { observable } from "mobx";

import {
  apiGetUsers,
  apiGetUsersMonitoringUsers,
  apiGetUsersPhysicians,
  FeatureFlagUpdate,
  FullUserResponse,
  LocaleTag,
  MonitoringUserResponse,
  SimpleUserResponse,
  UserFeatureFlag,
  UserResponse,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { putUserFeatureFlag } from "../../../api/user-feature-flags";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Merge } from "../../../utils/types";

export type UsersItem = Merge<
  FullUserResponse | UserResponse | MonitoringUserResponse
>;

/**
 * An in-progress user edit, fully populated: the user's own fields, plus the
 * physician and clinic assignments named as `PUT /users/{uuid}` takes them -
 * the read side calls the same relation `assigned_physicians` /
 * `assigned_clinics`.
 */
// A type alias, not an interface: `createPatch` takes a
// `Record<string, unknown>`, and only aliases get an implicit index signature.
export type UserEditDraft = {
  email: string | null;
  preferred_language: LocaleTag;
  connected_patient_id: number | null;
  physicians: string[];
  clinics: string[];
};

/**
 * What the edit dialog hands back on Save: only the keys that actually
 * changed, so an absent key means "leave it alone" rather than "clear it".
 * Worth keeping distinct from {@link UserEditDraft} - forwarding it wholesale
 * is safe, while picking keys off it sends `undefined` for the untouched ones.
 */
export type UserEditPatch = Partial<UserEditDraft>;

export class Users extends DataModel<UsersItem> {
  readonly idAttr = "uuid";
  @observable accessor userMapping: Record<string, string> = {};
  @observable accessor loading = false;
  readonly api: ApiConnector;

  // `api` is assigned by hand rather than declared as a constructor parameter
  // property: the decorator transform rewrites the `super()` call into a
  // sequence expression, and the parameter-property lowering that runs after
  // it then misses the call and hoists the assignment above `super()` — which
  // throws at construction time in a production build, nowhere else.
  constructor(api: ApiConnector) {
    super([], api.permissions, api.requireUserUUID());
    this.api = api;
    this.defaultFilter = {
      role: [
        "admin",
        "seerlinq-user",
        "study-physician",
        "physician",
        "patient",
      ],
    };

    // access
    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  dataInit() {
    super.init();
    this.getMapping();
  }

  async init() {
    await this.load(async () => (await apiGetUsers()).data.users);
  }

  async initPhysiciansOnly() {
    await this.load(async () => (await apiGetUsersPhysicians()).data.users);
  }

  /**
   * `assigned` are users already attached to the record being edited. The
   * endpoint only lists users that are currently eligible (monitoring active,
   * within the geography), so an assignment made earlier can be absent. Without
   * a row it still counts as selected but can never be unticked.
   */
  async initMonitoringUsers(
    country?: string,
    region?: number,
    assigned: SimpleUserResponse[] = [],
  ) {
    await this.load(async () => {
      // The API rejects a region without its country (400), which is what a
      // Country-only geography bypass would otherwise send.
      const { users } = (
        await apiGetUsersMonitoringUsers({
          query: {
            country,
            region: country === undefined ? undefined : region,
          },
        })
      ).data;
      const listed = new Set(users.map((user) => user.uuid));
      return [
        ...users,
        ...(assigned.filter((user) => !listed.has(user.uuid)) as UsersItem[]),
      ];
    });
  }

  private async load(fetch: () => Promise<UsersItem[]>) {
    this.loading = true;
    try {
      this.data = await fetch();
      this.dataInit();
    } finally {
      this.loading = false;
    }
  }

  getMapping() {
    for (const user of this.data) {
      this.userMapping[user.uuid] = user.username;
    }
  }

  /**
   * Deliberately not part of the edit dialog's form patch: that patch is a
   * shallow per-key diff, so a `feature_flags` key in it would send the whole
   * merged registry - the one thing {@link putUserFeatureFlag} exists to avoid.
   *
   * The list item is patched in place from the response, so there is no
   * refetch and no `updateUser` page reload between toggles.
   */
  async updateFeatureFlag(
    uuid: string,
    flag: UserFeatureFlag,
    update: FeatureFlagUpdate,
  ) {
    const flags = await putUserFeatureFlag(uuid, flag, update);
    const user = this.data.find((item) => item.uuid === uuid);
    if (user) {
      user.feature_flags = flags;
    }
  }
}
